<script setup lang="ts">
import { onMounted, onUnmounted, PropType, ref, nextTick, watch } from "vue";
import { Department, Participant, Project } from "../../types/PersologOnlineTypes";
import { useProjectDepartmentsStore } from "../../stores/projectDepartments";
import { useI18n } from "vue-i18n";
import EditableChip from "../common/EditableChip.vue";

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

const { t } = useI18n();

const emit = defineEmits(["update:departments"]);

const collapsed = ref(true);
const importMode = ref(false);
const showAddInput = ref(false);
const activeChipId = ref<string | number | null>(null);

const departmentList = ref<Department[]>([]);
const departmentImport = ref("");
const newDepartmentName = ref("");
const addInputRef = ref<{ $el: HTMLInputElement } | null>(null);

const departmentsStore = useProjectDepartmentsStore();

const fetchDepartments = async () => {
    await departmentsStore.ensureLoaded(props.project);
    departmentList.value = [...departmentsStore.getForProject(props.project.id!)].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Adds new departments from the input field
 * Treats commas and semicolons as separators, allows periods
 */
const addNewDepartment = () => {
    const input = newDepartmentName.value.trim();
    if (!input) {
        showAddInput.value = false;
        return;
    }
    
    // Split on commas and semicolons, but keep periods
    const departmentNames = input.split(/[,;]+/)
        .map(name => name.trim())
        .filter(name => name.length > 0);
    
    // Add each department if it doesn't already exist
    departmentNames.forEach(trimmedName => {
        if (!departmentList.value.some(d => d.name.toLowerCase() === trimmedName.toLowerCase())) {
            departmentList.value.push({ name: trimmedName });
        }
    });
    
    newDepartmentName.value = "";
    showAddInput.value = false;
    updateDepartments();
};

/**
 * Handle keydown events on the new department input
 */
const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
        event.preventDefault();
        addNewDepartment();
    } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelAddDepartment();
    }
};

/**
 * Show the add department input field
 */
const showAddDepartment = async () => {
    showAddInput.value = true;
    newDepartmentName.value = "";
    await nextTick();
    addInputRef.value?.$el?.focus();
};

/**
 * Cancel adding a department and hide input
 */
const cancelAddDepartment = () => {
    showAddInput.value = false;
    newDepartmentName.value = "";
};

/**
 * Updates the departments in the project
 * making a backend call to save the changes
 */
async function updateDepartments() {
    emit("update:departments", departmentList.value);
    await departmentsStore.save(props.project, departmentList.value as Department[]);
    // Refresh local list with server-assigned IDs so removable checks work correctly
    departmentList.value = [...departmentsStore.getForProject(props.project.id!)].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Updates a department name and auto-saves
 */
function updateDepartmentName(departmentId: number | undefined, newName: string) {
    const index = departmentList.value.findIndex(d => d.id === departmentId);
    if (index !== -1) {
        departmentList.value[index] = { ...departmentList.value[index], name: newName };
        updateDepartments();
    }
    activeChipId.value = null; // Close popup after save
}

/**
 * Toggles the action popup for a specific chip
 */
function handleToggleActions(chipId: string | number) {
    if (activeChipId.value === chipId) {
        activeChipId.value = null; // Close if already open
    } else {
        activeChipId.value = chipId; // Open this one and close others
    }
}

// Handle clicking outside to close popups
const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as Element;
    // Check if click is outside any chip or action popup
    if (!target.closest('.chip-container') && !target.closest('.action-popup')) {
        activeChipId.value = null;
    }
};

// Add/remove outside click listener based on popup state
const setupOutsideClickListener = () => {
    if (activeChipId.value == null) {
        document.removeEventListener('click', handleOutsideClick);
    } else {
        document.addEventListener('click', handleOutsideClick);
    }
};

// Watch activeChipId to manage outside click listener
watch(() => activeChipId.value, () => {
    setupOutsideClickListener();
});

function noParticipantHasDepartment(departmentId: number) {
    return !props.participants.some((p) => p.departmentId === departmentId);
}

function removeDepartment(departmentId: number | undefined, departmentName?: string) {
    if (departmentId) {
        departmentList.value = departmentList.value.filter((d) => d.id !== departmentId);
    } else if (departmentName) {
        departmentList.value = departmentList.value.filter((d) => d.name !== departmentName);
    }
    updateDepartments();
    activeChipId.value = null; // Close popup after delete
}

function importDepartments() {
    const departments = departmentImport.value.split(",");
    const newDepartments = departments.map((d) => ({ name: d.trim() }));

    const combinedDepartments = [...departmentList.value, ...newDepartments];
    departmentList.value = [...new Map(combinedDepartments.map((d) => [d.name.toLowerCase(), d])).values()];
    updateDepartments();
    importMode.value = false;
}

onMounted(async () => {
    await fetchDepartments();
});

onUnmounted(() => {
    document.removeEventListener('click', handleOutsideClick);
});
</script>

<template>
    <Panel
        :id="`departments_panel-${project.id}`"
        toggleable
        v-model:collapsed="collapsed"
        :header="t('project-configuration.projectDepartments')"
        class="mt-4"
        :pt="{ header: { onClick: () => { collapsed = !collapsed; }, style: 'cursor: pointer' }, pcToggleButton: { root: { onClick: (e: MouseEvent) => e.stopPropagation() } } }"
    >
    <Suspense>
        <div class="departments" data-cy="departments-container">
            <div class="chips-container" data-cy="departments-chips-container">
                <EditableChip
                    v-for="department in departmentList"
                    :key="department.id || department.name"
                    :chip-id="department.id || department.name"
                    :show-actions="activeChipId === (department.id || department.name)"
                    :label="department.name"
                    :removable="department.id ? noParticipantHasDepartment(department.id) : true"
                    @remove="removeDepartment(department.id ?? undefined, department.name)"
                    @update:label="updateDepartmentName(department.id ?? undefined, $event)"
                    @toggle-actions="handleToggleActions"
                    :data-cy="`department-chip-${department.name.toLowerCase().replace(/\s+/g, '-')}`"
                />
                <InputText
                    v-if="showAddInput"
                    ref="addInputRef"
                    v-model="newDepartmentName"
                    class="new-department-input"
                    :placeholder="departmentList.length == 0 ? t('project-configuration.tabs.departmentsPlaceholder') : 'Add department...'"
                    @keydown="handleKeyDown"
                    @blur="addNewDepartment"
                    size="small"
                    data-cy="new-department-input"
                />
                <Button
                    v-else
                    v-tooltip="t('project-details.departments.add')"
                    class="add-button p-button-rounded"
                    text
                    icon="pi pi-plus"
                    size="small"
                    aria-label="Add department"
                    data-cy="departments-add-button"
                    @click="showAddDepartment"
                />
            </div>
            <Button
                v-tooltip="t('project-details.departments.tooltip')"
                class="flex flex-item import-button p-button-rounded"
                text
                icon="pi pi-file-import"
                size="small"
                aria-label="Import departments"
                data-cy="departments-import-button"
                @click="importMode = true"
            />
        </div>

    </Suspense>
    </Panel>
    <Dialog
        v-model:visible="importMode"
        modal
        :header="t('project-configuration.tabs.addDepartments')"
        :style="{ width: '50rem' }"
        data-cy="departments-import-dialog"
    >
        <div class="my-4">{{ t("project-details.departments.import") }}</div>
        <div>
            <IconField>
                <FloatLabel>
                    <InputIcon class="pi pi-building" />
                    <label for="departmentImport">
                        {{ t("project-details.departments.heading") }}
                    </label>
                    <InputText 
                        v-model="departmentImport" 
                        class="w-full" 
                        id="departmentImport" 
                        autocomplete="off" 
                        :aria-label="t('accessibility.inputs.departments')"
                        data-cy="departments-import-input"
                    />
                </FloatLabel>
            </IconField>
        </div>
        <template #footer>
            <Button 
                :label="t('buttons.cancel')" 
                text 
                severity="secondary" 
                @click="importMode = false"
                data-cy="departments-import-cancel-button"
            />
            <Button 
                :label="t('buttons.save')" 
                @click="importDepartments()" 
                autofocus
                data-cy="departments-import-save-button"
            />
        </template>
    </Dialog>
</template>

<style scoped>
.departments {
    display: flex;
    align-items: center;
    margin-top: 0.5rem;
    gap: 0.5rem;
}

.chips-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    background-color: white;
    min-height: 2.5rem;
    width: auto;
}

.new-department-input {
    background: transparent;
    min-width: 80px;
    max-width: 200px;
    width: auto;
    font-size: 14px;
    margin-left: 0.4rem;
}

.new-department-input::placeholder {
    color: grey;
}

.add-button {
    background-color: none;
    color: var(--primary-color);
    cursor: pointer;
    padding: 0.25rem;
    margin-left: 0.4rem;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.import-button {
    border-radius: 16px;
}
</style>
